import moment from 'moment';
import { formatSmartCurrency } from './formatCurrency';

/**
 * Format a number as currency
 * @param {number} amount - The amount to format
 * @param {boolean} showCents - Whether to force showing cents (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showCents = false, currencyCode) => {
  return formatSmartCurrency(amount, {
    mode: 'long',
    forceDecimals: showCents,
    currencyCode,
  });
};

/**
 * Check if an income/expense item is obsolete (should be greyed out)
 * @param {Object} item - The item to check
 * @returns {boolean} True if item is obsolete
 */
export const isItemObsolete = (item) => {
  // Check for one-time items in the past
  const isOneTime = !item.recurrencePeriod || item.recurrencePeriod === 'none';
  const itemDate = item.date || item.dueDate;
  const isPastOneTime = isOneTime && itemDate && moment(itemDate).isBefore(moment(), 'day');

  // Check for dead recurring items - items that have recurrencePeriod but missing recurrenceFrequency
  // OR items with endDate in the past
  const hasRecurrencePeriod = item.recurrencePeriod && item.recurrencePeriod !== 'none';
  const missingRecurrenceFrequency = !item.recurrenceFrequency || item.recurrenceFrequency === 'none';
  const hasEndDateInPast = item.endDate && moment(item.endDate).isBefore(moment(), 'day');
  const isDeadRecurring = hasRecurrencePeriod && (missingRecurrenceFrequency || hasEndDateInPast);

  return isPastOneTime || isDeadRecurring;
};

/**
 * Filter cashFlowItems to get only base objects (no generated occurrences)
 * Base items either don't have originalId, or originalId equals id
 * Occurrences have originalId pointing to a different base item
 * @param {Array} items - Array of all cash flow items (including occurrences)
 * @returns {Array} Array of base items only
 */
export const getBaseItemsOnly = (items) => {
  if (!items || items.length === 0) return [];

  return items.filter(item => {
    // Base items either don't have originalId, or originalId equals id
    // Occurrences have originalId pointing to a different base item
    return !item.originalId || item.originalId === item.id;
  });
};

/**
 * Format balance data from FinanceDataContext into table rows
 * @param {Object} balances - Balances object from context
 * @param {Object} balanceHistory - Balance history object from context
 * @returns {Array} Array of formatted balance row objects
 */
export const formatBalanceData = (balances, balanceHistory) => {
  if (!balances || Object.keys(balances).length === 0) {
    return [];
  }

  const rows = [];
  const accountTypes = {
    checking: { icon: 'card-outline', label: 'Checking' },
    savings: { icon: 'wallet-outline', label: 'Savings' },
    credit: { icon: 'card-outline', label: 'Credit Card' },
    investment: { icon: 'trending-up-outline', label: 'Investment' },
    other: { icon: 'cash-outline', label: 'Other' },
  };

  Object.entries(balances).forEach(([accountId, accountData]) => {
    const balance = typeof accountData === 'number' ? accountData : accountData.amount || 0;
    const accountType = accountData.type || 'other';
    const accountName = accountData.title || accountData.name || accountId;
    const apy = accountData.apy || accountData.APY || accountData.growthRate || 0;
    const emoji = accountData.emoji || '🏦';

    // Calculate change (if balance history exists)
    let changeAmount = 0;
    let changePercent = 0;
    if (balanceHistory && balanceHistory[accountId]) {
      const history = Object.values(balanceHistory[accountId]).sort((a, b) =>
        moment(b.date).valueOf() - moment(a.date).valueOf()
      );
      if (history.length >= 2) {
        const current = history[0].balance;
        const previous = history[1].balance;
        changeAmount = current - previous;
        changePercent = previous !== 0 ? ((changeAmount / previous) * 100) : 0;
      }
    }

    rows.push({
      id: accountId,
      type: 'balance',
      icon: emoji,
      iconColor: balance >= 0 ? '#4CAF50' : '#F44336',
      title: accountName,
      subtitle: accountTypes[accountType]?.label || 'Account',
      amount: balance,
      apy: apy,
      metadata: {
        lastUpdated: moment().format('MMM D, YYYY'),
        changeAmount,
        changePercent,
        accountType,
        apy,
      },
      expandedData: balanceHistory?.[accountId] || null,
    });
  });

  // Sort by balance (highest first)
  return rows.sort((a, b) => b.amount - a.amount);
};

/**
 * Format income data from cashFlowItems into table rows
 * Shows ONLY base income objects (no occurrences)
 * @param {Array} cashFlowItems - Array of all cash flow items
 * @returns {Array} Array of formatted income row objects
 */
export const formatIncomeData = (cashFlowItems) => {
  if (!cashFlowItems || cashFlowItems.length === 0) {
    return [];
  }

  // Filter to income items AND base items only
  const baseIncomeItems = getBaseItemsOnly(
    cashFlowItems.filter(item => item.type === 'income')
  );

  const formatted = baseIncomeItems.map(item => {
    const emoji = item.emoji || '💰';

    // Determine if recurring
    const isRecurring = item.recurrenceFrequency &&
                       item.recurrencePeriod &&
                       item.recurrencePeriod !== 'none';

    // Format frequency for display
    let frequencyLabel = 'One-time';
    if (isRecurring) {
      const freq = parseInt(item.recurrenceFrequency) || 1;
      const period = item.recurrencePeriod || 'months';

      if (freq === 1) {
        // Convert 'months' -> 'Monthly', 'weeks' -> 'Weekly', etc.
        frequencyLabel = period.charAt(0).toUpperCase() + period.slice(0, -1) + 'ly';
      } else {
        frequencyLabel = `Every ${freq} ${period}`;
      }
    }

    return {
      id: item.id,
      type: 'income',
      icon: emoji,
      iconColor: '#4CAF50',
      title: item.title || 'Income',
      subtitle: frequencyLabel,
      amount: (item.amount || 0) / 100, // Convert cents to dollars
      metadata: {
        frequency: frequencyLabel,
        isRecurring,
        hasOccurrences: isRecurring,
        baseItem: item, // Store reference to base item
      },
      expandedData: null, // Will be populated when expanded
    };
  });

  // Sort by: 1) active vs obsolete, 2) recurring vs one-time, 3) amount
  return formatted.sort((a, b) => {
    const aIsObsolete = isItemObsolete(a.metadata.baseItem);
    const bIsObsolete = isItemObsolete(b.metadata.baseItem);

    // Active items come before obsolete items
    if (!aIsObsolete && bIsObsolete) return -1;
    if (aIsObsolete && !bIsObsolete) return 1;

    // Within same obsolete status, check recurring
    const aIsRecurring = a.metadata.baseItem?.recurrencePeriod &&
                        a.metadata.baseItem.recurrencePeriod !== 'none' &&
                        a.metadata.baseItem?.recurrenceFrequency &&
                        a.metadata.baseItem.recurrenceFrequency !== 'none';
    const bIsRecurring = b.metadata.baseItem?.recurrencePeriod &&
                        b.metadata.baseItem.recurrencePeriod !== 'none' &&
                        b.metadata.baseItem?.recurrenceFrequency &&
                        b.metadata.baseItem.recurrenceFrequency !== 'none';

    // Recurring items come first
    if (aIsRecurring && !bIsRecurring) return -1;
    if (!aIsRecurring && bIsRecurring) return 1;

    // Within same recurrence status, sort by amount (highest first)
    return b.amount - a.amount;
  });
};

/**
 * Format asset data into table rows
 * @param {Array} assets - Array of asset items
 * @returns {Array} Array of formatted asset row objects
 */
export const formatAssetData = (assets = []) => {
  // Return empty array if no data
  if (!assets || assets.length === 0) {
    return [];
  }

  const assetData = assets;

  return assetData.map(asset => {
    const currentValue = (asset.currentValue || 0) / 100;
    const purchaseValue = (asset.purchaseValue || 0) / 100;
    const changeAmount = currentValue - purchaseValue;
    const changePercent = purchaseValue !== 0 ? ((changeAmount / purchaseValue) * 100) : 0;

    // Calculate months held from purchase date
    const monthsHeld = asset.purchaseDate
      ? moment().diff(moment(asset.purchaseDate), 'months')
      : 0;

    // Use assetCategory from real data, fallback to assetType for mock data
    const category = asset.assetCategory || asset.assetType || 'other';

    return {
      id: asset.id,
      type: 'asset',
      icon: asset.emoji || '📦',
      iconColor: changeAmount >= 0 ? '#4CAF50' : '#FF9800',
      title: asset.title || 'Asset',
      subtitle: category,
      amount: currentValue,
      change: changePercent,
      metadata: {
        purchaseValue,
        changeAmount,
        changePercent,
        purchaseDate: asset.purchaseDate,
        assetCategory: category,
        monthsHeld,
        baseItem: asset, // Include original item for edit functionality
      },
      expandedData: {
        notes: asset.notes || null,
        depreciation: changePercent < 0 ? Math.abs(changePercent).toFixed(1) + '%' : null,
      },
    };
  }).sort((a, b) => b.amount - a.amount); // Sort by current value (highest first)
};

/**
 * Format liability data into table rows
 * @param {Array} liabilities - Array of liability items
 * @returns {Array} Array of formatted liability row objects
 */
export const formatLiabilityData = (liabilities = []) => {
  // Return empty array if no data
  if (!liabilities || liabilities.length === 0) {
    return [];
  }

  const liabilityData = liabilities;

  return liabilityData.map(liability => {
    const currentBalance = (liability.currentBalance || 0) / 100;
    const originalAmount = liability.originalAmount ? (liability.originalAmount / 100) : null;
    const monthlyPayment = (liability.monthlyPayment || 0) / 100;
    const interestRate = liability.interestRate || 0;

    // Calculate payoff timeline
    let monthsToPayoff = 0;
    let estimatedPayoffDate = 'N/A';

    if (currentBalance > 0 && monthlyPayment > 0) {
      const monthlyRate = interestRate / 12 / 100;

      if (monthlyRate === 0) {
        // No interest, simple division
        monthsToPayoff = Math.ceil(currentBalance / monthlyPayment);
      } else {
        // Check if payment covers interest
        const monthlyInterest = currentBalance * monthlyRate;
        if (monthlyPayment <= monthlyInterest) {
          // Payment doesn't cover interest - will never pay off
          monthsToPayoff = Infinity;
          estimatedPayoffDate = 'Never (payment < interest)';
        } else {
          // Calculate using loan payoff formula
          monthsToPayoff = Math.log(monthlyPayment / (monthlyPayment - currentBalance * monthlyRate)) / Math.log(1 + monthlyRate);
          monthsToPayoff = Math.ceil(monthsToPayoff);
        }
      }

      // Format payoff date
      if (monthsToPayoff !== Infinity && monthsToPayoff > 0) {
        estimatedPayoffDate = moment().add(monthsToPayoff, 'months').format('MMM YYYY');
      }
    }

    // Use liabilityCategory from real data, fallback to liabilityType for mock data
    const category = liability.liabilityCategory || liability.liabilityType || 'other';

    // Credit card utilization
    const creditLimit = (liability.creditLimit || 0) / 100;
    const interestPaidYTD = (liability.interestPaidYTD || 0) / 100;
    let utilization = 0;
    let availableCredit = 0;
    if (category === 'credit_card' && creditLimit > 0) {
      utilization = (currentBalance / creditLimit) * 100;
      availableCredit = creditLimit - currentBalance;
    }

    return {
      id: liability.id,
      type: 'liability',
      icon: liability.emoji || '💰',
      iconColor: '#F44336', // Red for liabilities
      title: liability.title || 'Liability',
      subtitle: category,
      amount: currentBalance,
      metadata: {
        originalAmount,
        interestRate,
        monthlyPayment,
        creditLimit,
        interestPaidYTD,
        utilization: category === 'credit_card' && creditLimit > 0 ? utilization : null,
        availableCredit: category === 'credit_card' && creditLimit > 0 ? availableCredit : null,
        liabilityCategory: category,
        monthsToPayoff: monthsToPayoff === Infinity ? null : monthsToPayoff,
        estimatedPayoffDate,
        baseItem: liability, // Include original item for edit functionality
      },
      expandedData: {
        monthlyPayment: monthlyPayment > 0 ? `$${monthlyPayment.toFixed(2)}` : null,
        estimatedPayoffDate,
        interestRate: interestRate > 0 ? `${interestRate.toFixed(2)}%` : null,
        utilization: category === 'credit_card' && creditLimit > 0 ? `${utilization.toFixed(1)}% used` : null,
        availableCredit: category === 'credit_card' && creditLimit > 0 ? `$${availableCredit.toFixed(2)} available` : null,
        interestPaidYTD: interestPaidYTD > 0 ? `$${interestPaidYTD.toFixed(2)} YTD interest` : null,
        notes: liability.notes || null,
      },
    };
  }).sort((a, b) => b.amount - a.amount); // Sort by amount owed (highest first)
};

/**
 * Format savings data from cashFlowItems into table rows
 * @param {Array} cashFlowItems - Array of all cash flow items
 * @returns {Array} Array of formatted savings row objects
 */
export const formatSavingsData = (cashFlowItems) => {
  if (!cashFlowItems || cashFlowItems.length === 0) {
    return [];
  }

  const savingsItems = cashFlowItems.filter(item =>
    item.type === 'savings' || item.type === 'goal'
  );

  return savingsItems.map(item => {
    const currentAmount = (item.currentAmount || item.saved || 0) / 100; // Convert cents to dollars
    const targetAmount = (item.targetAmount || item.amount || 0) / 100; // Convert cents to dollars
    const progress = targetAmount > 0 ? (currentAmount / targetAmount) * 100 : 0;
    const emoji = item.emoji || '🎯';

    return {
      id: item.id || `savings-${item.title}`,
      type: 'savings',
      icon: emoji,
      iconColor: '#2196F3',
      title: item.title || 'Savings Goal',
      subtitle: `${Math.round(progress)}% of ${formatCurrency(targetAmount, false)}`,
      amount: currentAmount,
      metadata: {
        targetAmount,
        progress: Math.round(progress),
        targetDate: item.targetDate,
      },
      expandedData: {
        contributions: item.contributions || [],
        monthlyContribution: item.monthlyContribution,
        notes: item.notes,
      },
    };
  });
};

/**
 * Format expense data from cashFlowItems into table rows
 * Shows ONLY base expense objects (no occurrences)
 * @param {Array} cashFlowItems - Array of all cash flow items
 * @returns {Array} Array of formatted expense row objects
 */
export const formatExpenseData = (cashFlowItems) => {
  if (!cashFlowItems || cashFlowItems.length === 0) {
    return [];
  }

  // Filter to expense items AND base items only
  const baseExpenseItems = getBaseItemsOnly(
    cashFlowItems.filter(item => item.type === 'expense')
  );

  const formatted = baseExpenseItems.map(item => {
    const emoji = item.emoji || '💳';

    // Determine if recurring
    const isRecurring = item.recurrenceFrequency &&
                       item.recurrencePeriod &&
                       item.recurrencePeriod !== 'none';

    // Format frequency for display
    let frequencyLabel = 'One-time';
    if (isRecurring) {
      const freq = parseInt(item.recurrenceFrequency) || 1;
      const period = item.recurrencePeriod || 'months';

      if (freq === 1) {
        // Convert 'months' -> 'Monthly', 'weeks' -> 'Weekly', etc.
        frequencyLabel = period.charAt(0).toUpperCase() + period.slice(0, -1) + 'ly';
      } else {
        frequencyLabel = `Every ${freq} ${period}`;
      }
    }

    return {
      id: item.id,
      type: 'expense',
      icon: emoji,
      iconColor: '#F44336',
      title: item.title || 'Expense',
      subtitle: frequencyLabel,
      amount: (item.amount || 0) / 100, // Convert cents to dollars
      metadata: {
        category: item.budgetCategoryId || item.category || 'Uncategorized',
        isRecurring,
        hasOccurrences: isRecurring,
        baseItem: item, // Store reference to base item
      },
      expandedData: null, // Will be populated when expanded
    };
  });

  // Sort by: 1) active vs obsolete, 2) recurring vs one-time, 3) amount
  return formatted.sort((a, b) => {
    const aIsObsolete = isItemObsolete(a.metadata.baseItem);
    const bIsObsolete = isItemObsolete(b.metadata.baseItem);

    // Active items come before obsolete items
    if (!aIsObsolete && bIsObsolete) return -1;
    if (aIsObsolete && !bIsObsolete) return 1;

    // Within same obsolete status, check recurring
    const aIsRecurring = a.metadata.baseItem?.recurrencePeriod &&
                        a.metadata.baseItem.recurrencePeriod !== 'none' &&
                        a.metadata.baseItem?.recurrenceFrequency &&
                        a.metadata.baseItem.recurrenceFrequency !== 'none';
    const bIsRecurring = b.metadata.baseItem?.recurrencePeriod &&
                        b.metadata.baseItem.recurrencePeriod !== 'none' &&
                        b.metadata.baseItem?.recurrenceFrequency &&
                        b.metadata.baseItem.recurrenceFrequency !== 'none';

    // Recurring items come first
    if (aIsRecurring && !bIsRecurring) return -1;
    if (!aIsRecurring && bIsRecurring) return 1;

    // Within same recurrence status, sort by amount (highest first)
    return b.amount - a.amount;
  });
};

/**
 * Combine all data types into a single unified array
 * @param {Array} balances - Formatted balance data
 * @param {Array} income - Formatted income data
 * @param {Array} expenses - Formatted expense data
 * @param {Array} assets - Formatted asset data
 * @param {Array} liabilities - Formatted liability data
 * @param {Array} savings - Formatted savings data
 * @returns {Array} Combined array with section headers
 */
export const formatAllData = (balances, income, expenses, assets, liabilities, savings) => {
  const combined = [];

  if (balances.length > 0) {
    combined.push({
      id: 'section-balances',
      sectionHeader: true,
      title: 'Balances',
      count: balances.length,
    });
    combined.push(...balances);
  }

  if (income.length > 0) {
    combined.push({
      id: 'section-income',
      sectionHeader: true,
      title: 'Income',
      count: income.length,
    });
    combined.push(...income);
  }

  if (expenses.length > 0) {
    combined.push({
      id: 'section-expenses',
      sectionHeader: true,
      title: 'Expenses',
      count: expenses.length,
    });
    combined.push(...expenses);
  }

  if (assets.length > 0) {
    combined.push({
      id: 'section-assets',
      sectionHeader: true,
      title: 'Assets',
      count: assets.length,
    });
    combined.push(...assets);
  }

  if (liabilities.length > 0) {
    combined.push({
      id: 'section-liabilities',
      sectionHeader: true,
      title: 'Liabilities',
      count: liabilities.length,
    });
    combined.push(...liabilities);
  }

  if (savings.length > 0) {
    combined.push({
      id: 'section-savings',
      sectionHeader: true,
      title: 'Savings',
      count: savings.length,
    });
    combined.push(...savings);
  }

  return combined;
};

/**
 * Sort data by different criteria
 * @param {Array} data - Array of data rows
 * @param {string} sortType - Sort type: 'amount-high', 'amount-low', 'date-new', 'date-old', 'name-az', 'name-za'
 * @returns {Array} Sorted array
 */
export const sortData = (data, sortType) => {
  const sorted = [...data];

  // Don't sort section headers
  const sections = sorted.filter(item => item.sectionHeader);
  const items = sorted.filter(item => !item.sectionHeader);

  switch (sortType) {
    case 'amount-high':
      items.sort((a, b) => {
        // For income and expenses, prioritize active items over obsolete
        if (a.type === 'income' || a.type === 'expense') {
          const aIsObsolete = isItemObsolete(a.metadata.baseItem);
          const bIsObsolete = isItemObsolete(b.metadata.baseItem);

          // Active items come before obsolete items
          if (!aIsObsolete && bIsObsolete) return -1;
          if (aIsObsolete && !bIsObsolete) return 1;

          // Within same obsolete status, prioritize recurring items first
          const aIsRecurring = a.metadata.baseItem?.recurrencePeriod &&
                              a.metadata.baseItem.recurrencePeriod !== 'none' &&
                              a.metadata.baseItem?.recurrenceFrequency &&
                              a.metadata.baseItem.recurrenceFrequency !== 'none';
          const bIsRecurring = b.metadata.baseItem?.recurrencePeriod &&
                              b.metadata.baseItem.recurrencePeriod !== 'none' &&
                              b.metadata.baseItem?.recurrenceFrequency &&
                              b.metadata.baseItem.recurrenceFrequency !== 'none';

          // Recurring items come first
          if (aIsRecurring && !bIsRecurring) return -1;
          if (!aIsRecurring && bIsRecurring) return 1;
        }

        // Within same recurrence status (or for other types), sort by amount
        return b.amount - a.amount;
      });
      break;
    case 'amount-low':
      items.sort((a, b) => {
        // For income and expenses, prioritize active items over obsolete
        if (a.type === 'income' || a.type === 'expense') {
          const aIsObsolete = isItemObsolete(a.metadata.baseItem);
          const bIsObsolete = isItemObsolete(b.metadata.baseItem);

          // Active items come before obsolete items
          if (!aIsObsolete && bIsObsolete) return -1;
          if (aIsObsolete && !bIsObsolete) return 1;

          // Within same obsolete status, prioritize recurring items first
          const aIsRecurring = a.metadata.baseItem?.recurrencePeriod &&
                              a.metadata.baseItem.recurrencePeriod !== 'none' &&
                              a.metadata.baseItem?.recurrenceFrequency &&
                              a.metadata.baseItem.recurrenceFrequency !== 'none';
          const bIsRecurring = b.metadata.baseItem?.recurrencePeriod &&
                              b.metadata.baseItem.recurrencePeriod !== 'none' &&
                              b.metadata.baseItem?.recurrenceFrequency &&
                              b.metadata.baseItem.recurrenceFrequency !== 'none';

          // Recurring items come first
          if (aIsRecurring && !bIsRecurring) return -1;
          if (!aIsRecurring && bIsRecurring) return 1;
        }

        // Within same recurrence status (or for other types), sort by amount
        return a.amount - b.amount;
      });
      break;
    case 'date-new':
      items.sort((a, b) => {
        const aDate = a.metadata?.dateValue || 0;
        const bDate = b.metadata?.dateValue || 0;
        return bDate - aDate;
      });
      break;
    case 'date-old':
      items.sort((a, b) => {
        const aDate = a.metadata?.dateValue || 0;
        const bDate = b.metadata?.dateValue || 0;
        return aDate - bDate;
      });
      break;
    case 'name-az':
      items.sort((a, b) => a.title.localeCompare(b.title));
      break;
    case 'name-za':
      items.sort((a, b) => b.title.localeCompare(a.title));
      break;
    default:
      // No sorting
      break;
  }

  return items;
};

/**
 * Filter data based on criteria
 * @param {Array} data - Array of data rows
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered array
 */
export const filterData = (data, filters) => {
  if (!filters || Object.keys(filters).length === 0) {
    return data;
  }

  let filtered = [...data];

  // Filter by type
  if (filters.type && filters.type !== 'all') {
    filtered = filtered.filter(item => item.type === filters.type);
  }

  // Filter by date range
  if (filters.dateRange) {
    const now = moment();
    let startDate;

    switch (filters.dateRange) {
      case 'this-month':
        startDate = now.clone().startOf('month');
        break;
      case 'last-3-months':
        startDate = now.clone().subtract(3, 'months');
        break;
      case 'last-6-months':
        startDate = now.clone().subtract(6, 'months');
        break;
      case 'this-year':
        startDate = now.clone().startOf('year');
        break;
      default:
        startDate = null;
    }

    if (startDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.metadata?.dateValue;
        return !itemDate || moment(itemDate).isAfter(startDate);
      });
    }
  }

  // Filter by search keyword
  if (filters.search && filters.search.trim() !== '') {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(item =>
      item.title.toLowerCase().includes(searchLower) ||
      item.subtitle?.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Calculate net worth and organize asset/liability breakdown
 * @param {Array} assets - Formatted asset data from formatAssetData()
 * @param {Array} liabilities - Formatted liability data from formatLiabilityData()
 * @returns {Object} Object containing net worth calculations and ratios
 */
export const calculateNetWorth = (assets, liabilities) => {
  const totalAssets = assets.reduce((sum, asset) => sum + asset.amount, 0);
  const totalLiabilities = liabilities.reduce((sum, liability) => sum + liability.amount, 0);
  const netWorth = totalAssets - totalLiabilities;

  const total = totalAssets + totalLiabilities || 1; // Prevent division by zero

  return {
    netWorth,
    totalAssets,
    totalLiabilities,
    assetRatio: (totalAssets / total) * 100,
    liabilityRatio: (totalLiabilities / total) * 100,
  };
};

/**
 * Organize assets and liabilities by type for breakdown display
 * @param {Array} assets - Formatted asset data
 * @param {Array} liabilities - Formatted liability data
 * @returns {Object} Organized data structure with grouped items
 */
export const organizeNetWorthData = (assets, liabilities) => {
  // Group assets by type
  const assetsByType = {};
  assets.forEach(asset => {
    const assetType = asset.metadata?.assetType || 'Other';
    if (!assetsByType[assetType]) {
      assetsByType[assetType] = [];
    }
    assetsByType[assetType].push(asset);
  });

  // Get top 4 assets (already sorted by formatAssetData)
  const topAssets = assets.slice(0, 4);

  // Group liabilities by type
  const liabilitiesByType = {};
  liabilities.forEach(liability => {
    const liabilityType = liability.metadata?.liabilityType || 'Other';
    if (!liabilitiesByType[liabilityType]) {
      liabilitiesByType[liabilityType] = [];
    }
    liabilitiesByType[liabilityType].push(liability);
  });

  // Get top 4 liabilities (already sorted by formatLiabilityData)
  const topLiabilities = liabilities.slice(0, 4);

  return {
    topAssets,
    topLiabilities,
    assetsByType,
    liabilitiesByType,
    allAssets: assets,
    allLiabilities: liabilities,
  };
};
